<template>
  <article class="toolbar-container">
    <article v-if="activityDataVisible || (usernameVisible && username)">
      <article :aria-label="userAndProgressHeading" id="user-and-progress-heading" role="region">
        <p v-if="usernameVisible && username" class="username resizableText">{{ username }}</p>
        <article v-if="activityDataVisible" class="activity-data">
          <p class="resizableText">Week {{ nextWeek }}</p>
          <p class="resizableText">#{{ nextASN }}</p>
        </article>
      </article>
    </article>
    <ul v-if="kwtMode !== 'previewLite'" class="kwt-toolbar">
      <li :class="['toolbar-row', { show: fullScreenVisible }]">
        <button @click="toggleFullscreen" aria-label="Fullscreen" class="pure-button" tabindex="2">
          <fullscreen-icon :is-fullscreen="isFullscreen"> </fullscreen-icon>
        </button>
      </li>
      <li :class="['toolbar-row', { show: restartVisible }]">
        <button
          @click="restartActivity(true)"
          aria-label="Restart"
          class="pure-button"
          tabindex="3"
        >
          <restart-icon></restart-icon>
        </button>
      </li>
      <template v-if="kwtMode !== 'preview'">
        <li :class="['toolbar-row', { show: logoutVisible }]">
          <a @click="logout" aria-label="Log Out" class="pure-button" tabindex="4">
            <stop-icon></stop-icon>
          </a>
        </li>
        <li :class="['toolbar-row', { show: videoVisible }]">
          <button
            @click="showVideo"
            aria-label="Video Instructions"
            class="pure-button"
            tabindex="5"
          >
            <video-icon></video-icon>
          </button>
        </li>
        <li :class="['toolbar-row', { show: userProgressVisible }]">
          <button @click="showProgress" aria-label="Progress" class="pure-button" tabindex="6">
            <progress-icon></progress-icon>
          </button>
        </li>
        <li
          :class="[
            'toolbar-row',
            'volume-toolbar-row',
            { show: restartVisible || logoutVisible || fullScreenVisible }
          ]"
        >
          <button
            @click="setShowVolumeControls"
            :aria-label="`Volume Control - ${soundMuted ? 'Muted' : `Volume level - ${volume}%`}`"
            class="pure-button"
            tabindex="7"
          >
            <volume-icon height="52px" width="52px" v-if="!soundMuted"></volume-icon>
            <muted-icon height="52px" width="52px" v-if="soundMuted"></muted-icon>
          </button>
          <div
            id="volume-slider"
            tabindex="-1"
            role="button"
            :class="['toolbar-row', { show: showVolumeControls }]"
          >
            <div id="volume-slider-controls">
              <button
                @click="() => updateVolume(volume - 20)"
                aria-label="Mute or unmute volume"
                class="pure-button volume-down-button"
              >
                <volume-down-icon
                  height="42px"
                  width="42px"
                  :style="{ display: !soundMuted ? 'block' : 'none' }"
                ></volume-down-icon>
                <muted-icon
                  height="42px"
                  width="42px"
                  :style="{ display: soundMuted ? 'block' : 'none' }"
                ></muted-icon>
              </button>
              <volume-slider />
              <button
                @click="() => updateVolume(volume + 20)"
                aria-label="Mute or unmute volume"
                class="pure-button volume-up-button"
              >
                <volume-up-icon height="42px" width="42px"></volume-up-icon>
              </button>
            </div>
          </div>
        </li>
        <li :class="['toolbar-row', { show: isCertVisible }]">
          <button @click="showCert" aria-label="Certificate" class="pure-button" tabindex="8">
            <certificate-icon></certificate-icon>
          </button>
        </li>
      </template>
    </ul>
  </article>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';

import screenfull from 'screenfull';
import FullscreenIcon from 'components/icons/FullscreenIcon.vue';
import RestartIcon from 'components/icons/RestartIcon.vue';
import StopIcon from 'components/icons/StopIcon.vue';
import ProgressIcon from 'components/icons/ProgressIcon.vue';
import VideoIcon from 'components/icons/VideoIcon.vue';
import CertificateIcon from 'components/icons/CertificateIcon.vue';
import VolumeIcon from 'components/icons/VolumeControls/VolumeIcon.vue';
import VolumeDownIcon from 'components/icons/VolumeControls/VolumeDownIcon.vue';
import VolumeUpIcon from 'components/icons/VolumeControls/VolumeUpIcon.vue';
import MutedIcon from 'components/icons/VolumeControls/VolumeMutedIcon.vue';
import VolumeSlider from 'components/inputs/VolumeSlider.vue';
import { deleteCookie } from 'src/utils/browser-util';

const activity = createNamespacedHelpers('activity');
const student = createNamespacedHelpers('student');
const application = createNamespacedHelpers('application');
const keyboard = createNamespacedHelpers('keyboard');

export default {
  components: {
    FullscreenIcon,
    RestartIcon,
    StopIcon,
    VideoIcon,
    ProgressIcon,
    CertificateIcon,
    VolumeIcon,
    VolumeDownIcon,
    VolumeUpIcon,
    MutedIcon,
    VolumeSlider
  },

  computed: {
    ...activity.mapState({
      hasVideo: state => state.data.showVideo,
      playVideo: state => state.data.playVideo,
      isSpotCheck: state => Boolean(state.data.spotCheck)
    }),
    ...activity.mapGetters(['activityInProgress', 'templateVideo']),
    ...application.mapGetters(['logoutUrl']),
    ...student.mapState(['username', 'homeUseToken', 'token', 'accessibilitySettings']),
    ...student.mapState({
      nextWeek: ({ position: { next: { week } = {} } = {} }) => week || 1,
      nextASN: ({ position: { next: { asn } = {} } = {} }) => asn || 1,
      isSpeedyKid: ({ position: { next: { theme: { id } = {} } = {} } = {} }) => id === 'SpeedyKid',
      lastPosition: state => state.position.last || {},
      nextPosition: state => state.position.next || {},
      accessibilitySettings: state => state.accessibilitySettings,
      volume: state => state.accessibilitySettings.audioSettings.volume,
      soundMuted: state => state.accessibilitySettings.audioSettings.muteSounds,
      sessionId: state => state.sessionId
    }),

    ...application.mapState(['device', 'kwtMode', 'toolbarMode']),

    isCertVisible() {
      const {
        lastPosition: {
          progress: { curriculum: { position, total } = {} } = {},
          theme: { id: prevThemeId } = {}
        },
        nextPosition: { theme: { id: themeId } = {} }
      } = this;

      return (
        position &&
        total &&
        position === total &&
        themeId === 'SpeedyKid' &&
        prevThemeId !== 'SpeedyKid'
      );
    },

    isMobile() {
      return this.device.isMobile;
    },

    userAndProgressHeading() {
      return `${this.username || ''}${this.nextWeek ? ` Week ${this.nextWeek}` : ''}${
        this.nextASN ? ` Activity #${this.nextASN}` : ''
      }`.trim();
    },

    isSpotCheckResultsPage() {
      return this.$router.history.current.name === 'SpotcheckResults';
    }
  },

  watch: {
    toolbarMode(newVal) {
      this.setToolbar(this.toolbarMode);
    },
    kwtMode(newVal) {
      if (newVal === 'tester' && !!this.username) {
        this.logoutVisible = true;
      }
    },

    sessionId(newVal) {
      if (newVal) {
        this.logoutUrlWithSession = this.logoutUrl;
      }
    },
    $route(to, from) {
      if (to.fullPath === '/spotcheck-result') {
        this.restartVisible = false;
      }
    }
  },

  data() {
    if (screenfull.enabled) {
      screenfull.on('change', () => {
        this.isFullscreen = screenfull.isFullscreen;
      });
    }

    return {
      certVisible: false,
      isFullscreen: screenfull.enabled ? screenfull.isFullscreen : false,
      videoVisible: false,
      logoutVisible: false,
      userProgressVisible: false,
      usernameVisible: false,
      activityDataVisible: false,
      restartVisible: false,
      isSafari: false,
      fullScreenVisible: false,
      oldPlayVideo: null,
      showVolumeControls: false,
      logoutUrlWithSession: this.logoutUrl
    };
  },

  methods: {
    ...activity.mapActions(['restartActivity']),

    ...activity.mapMutations(['resetActivityStore', 'resetProgress', 'resetViewedIntro']),

    ...application.mapMutations(['setModal', 'resetApplication']),

    ...student.mapMutations(['resetStudent', 'setAccessibilitySettings']),
    ...student.mapActions(['saveStudentSettings']),

    ...keyboard.mapMutations(['resetKeyboard']),

    setToolbar(mode) {
      switch (mode) {
        case 'inActivity':
          this.setInActivityToolbar();
          break;
        case 'award':
          this.setAwardToolbar();
          break;
        case 'inActivityPanel':
          this.setInActivityPanelToolbar();
          break;
        case 'fullScreenOnly':
          this.setFullScreenToolbar();
          break;
        default:
          this.hideAll();
      }
    },

    setFullScreenToolbar() {
      this.hideAll();
      this.isFullScreenOK();
    },

    setInActivityToolbar() {
      this.hideAll();
      this.fullScreenVisible = !this.isMobile;
      this.restartVisible = true;
    },

    setAwardToolbar() {
      this.hideAll();
      this.fullScreenVisible = !this.isMobile;
      this.userProgressVisible = true;
      this.setToolbarTransition();
      if (this.kwtMode === 'curriculum') {
        this.calculateWeekData();
      }
    },

    setInActivityPanelToolbar() {
      this.hideAll();
      this.fullScreenVisible = !this.isMobile;
      this.restartVisible = true;
      this.userProgressVisible = true;

      this.checkVideo();
      this.setToolbarTransition();
      if (this.kwtMode === 'curriculum') {
        this.calculateWeekData();
      }
    },

    hideAll() {
      this.userProgressVisible = false;
      this.logoutVisible = false;
      this.usernameVisible = false;
      this.restartVisible = false;
      this.activityDataVisible = false;
      this.videoVisible = false;
      this.fullScreenVisible = false;
      this.certVisible = false;
    },

    async toggleFullscreen() {
      if (screenfull.enabled) {
        screenfull.toggle();
      }
    },

    isFullScreenOK() {
      this.fullScreenVisible = !this.isMobile;
    },

    // Show Logout, Progress, and username
    setToolbarTransition() {
      this.isFullScreenOK();
      // Show logout only if user is NOT a token/clever user
      if (!this.homeUseToken && !this.token) {
        this.logoutVisible = true;
      }

      this.userProgressVisible = true;
      this.usernameVisible = true;
      this.restartVisible = false;

      if (this.isSpotCheck) {
        this.hideToolbar = true;
      } else {
        this.hideToolbar = false;
      }
    },

    checkVideo() {
      if (this.templateVideo) {
        this.videoVisible = true;
      }
    },

    setToolbarAssessment() {
      this.hideAll();
      this.logoutVisible = true;
    },

    calculateWeekData() {
      if (this.kwtMode === 'curriculum' && !this.isSpeedyKid) {
        this.activityDataVisible = true;
      }
    },

    logout() {
      deleteCookie('session');
      window.location.href = this.logoutUrlWithSession;
    },

    setToolbarRestart() {
      this.userProgressVisible = false;
      this.logoutVisible = false;
      this.usernameVisible = false;
      this.restartVisible = false;
    },

    showVideo() {
      if (this.templateVideo) {
        this.setModal({ type: 'video', data: this.templateVideo });
      }
    },

    showVideoFirstTime() {
      if (this.oldPlayVideo !== this.playVideo) {
        this.setModal({ type: 'video', data: this.playVideo });
        this.oldPlayVideo = this.playVideo;
      }
    },

    showProgress() {
      this.setModal({ type: 'progress', data: null });
    },

    showCert() {
      this.setModal({ type: 'cert', data: '' });
    },

    setControlsOff(e) {
      const sliderEl = document.getElementById('volume-slider');
      if (!sliderEl) return;
      if (!sliderEl.contains(e.target)) {
        this.showVolumeControls = false;
        document.removeEventListener('click', this.setControlsOff);
      }
    },

    setShowVolumeControls() {
      if (!this.showVolumeControls) {
        this.showVolumeControls = true;
        setTimeout(() => {
          document.addEventListener('click', this.setControlsOff);
        }, [500]);
      } else {
        this.showVolumeControls = false;
      }
    },

    toggleAudio() {
      const clonedSettings = { ...this.accessibilitySettings };
      clonedSettings.audioSettings.muteSounds = !clonedSettings.audioSettings.muteSounds;
      this.setAccessibilitySettings(clonedSettings);
      this.saveStudentSettings();
    },

    updateVolume(volumeNumber) {
      const volume = volumeNumber < 0 ? 0 : volumeNumber > 100 ? 100 : volumeNumber;
      const clonedSettings = { ...this.accessibilitySettings };
      clonedSettings.audioSettings.volume = volume;
      if (this.soundMuted && volume > 0) {
        clonedSettings.audioSettings.muteSounds = false;
      }
      if (!this.soundMuted && volume === 0) {
        clonedSettings.audioSettings.muteSounds = true;
      }
      this.setAccessibilitySettings(clonedSettings);
      this.saveStudentSettings();
    }
  }
};
</script>

<style lang="scss" scoped>
.toolbar-container {
  column-gap: 8px;
  display: flex;
  flex-direction: row;
  margin: 10px 8px;
  padding: 0px;
  position: fixed;
  right: 5px;
  text-align: right;
  top: 5px;
  z-index: 1000;
}

ul.kwt-toolbar {
  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 0px;
  padding: 0px;

  li {
    margin-bottom: 2.5px;
  }

  @media (max-width: 986px) {
    margin: 0px;

    li {
      margin-bottom: 8px;
    }

    button,
    li,
    svg {
      height: 50px;
      width: 50px;
    }
  }
  @media (max-width: 863px) {
    margin: 0px;

    li {
      margin-bottom: 6px;
    }

    button,
    li,
    svg {
      height: 37.5px;
      width: 37.5px;
    }

    #volume-slider-controls {
      height: 28.5px !important;
    }
  }
  @media (max-width: 690px) {
    margin: 0px;

    li {
      margin-bottom: 4px;
    }

    button,
    li,
    svg {
      height: 25px;
      width: 25px;
    }

    #volume-slider-controls {
      height: 14px !important;
    }
  }
  @media (max-width: 432px) {
    margin: 0px;

    li {
      margin-bottom: 2px;
    }

    button,
    li,
    svg {
      height: 12.5px;
      width: 12.5px;
    }

    #volume-slider {
      top: 50% !important;
    }

    #volume-slider-controls {
      height: 5px !important;
      width: 80px !important;
    }
  }

  .volume-toolbar-row {
    #volume-slider {
      border-radius: 10px;
      max-height: 35px;
      height: 100%;
      width: max-content;
      position: absolute;
      top: 15%;
      right: -300px;
      -webkit-animation: slideIn 0.5s forwards;
      animation: slideIn 0.5s forwards;
    }

    #volume-slider-controls {
      display: flex;
      height: 100%;
      width: 100%;
      max-height: 35px;

      position: relative;
      align-items: center;
      .volume-up-button {
        margin-left: -2%;
        margin-top: 2px;

        svg {
          margin-left: -2%;
        }
      }

      .volume-down-button {
        margin-right: -2%;
        margin-top: 2px;
        z-index: 1;

        svg {
          margin-right: -2px;
        }
      }

      .timeline {
        width: 116px;
        border-top: 1px solid black;
        border-bottom: 1px solid black;
        background-color: white;
      }
    }
  }

  @keyframes slideIn {
    100% {
      right: 100%;
    }
  }

  .toolbar-row {
    position: relative;
    display: none;

    &.show {
      display: block;
    }

    button {
      padding: 0px;
      text-align: center;
    }

    a {
      cursor: pointer;
    }
  }
}

#user-and-progress-heading {
  background: white;
  border-radius: 4px;
  padding: 4px;

  .username {
    text-align: right;
    font-size: 14px;
    font-family: 'hwtfutura-semibold';
    margin: 5px 0 0 0;
    padding: 0;
    color: black;
    font-weight: bold;
  }

  .activity-data {
    font-size: 11px;
    color: #757575;
    text-align: right;
    p {
      white-space: nowrap;
      font-family: 'hwtfutura-semibold';
      margin: 0;
    }
  }
}
</style>
